/**
 * Billing and Charges Utility Functions
 * Handles calculations and formatting for billing and charge operations
 *
 * @module utils/billingAndCharges
 */

import { BILLING_STATUS, CHARGE_STATUS } from '@/constants/billingAndCharges'

/**
 * Calculate days until a due date from today
 * Negative = overdue
 * @param {string} dueDate - Date in format 'YYYY-MM-DD'
 * @returns {number} Days until due date
 */
export const calculateDaysUntilDue = (dueDate) => {
  if (!dueDate) return null

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const due = new Date(dueDate)
  due.setHours(0, 0, 0, 0)

  const timeDiff = due - today
  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24))

  return daysDiff
}

/**
 * Determine if a date is overdue
 * @param {string} dueDate - Date in format 'YYYY-MM-DD'
 * @returns {boolean} True if overdue (today > dueDate)
 */
export const isOverdue = (dueDate) => {
  return calculateDaysUntilDue(dueDate) < 0
}

/**
 * Determine if a date is coming due within 15 days
 * @param {string} dueDate - Date in format 'YYYY-MM-DD'
 * @returns {boolean} True if due within 15 days or overdue
 */
export const isDueWithin15Days = (dueDate) => {
  const daysUntil = calculateDaysUntilDue(dueDate)
  return daysUntil !== null && daysUntil <= 15
}

/**
 * Determine billing status from API response
 * - Billed: billing exists AND canceled_at IS NULL
 * - Unbilled: billing is null OR canceled_at has value
 * @param {Object} billingData - Billing object from API
 * @returns {string} BILLING_STATUS value
 */
export const getBillingStatus = (billingData) => {
  if (!billingData || billingData.canceled_at) {
    return BILLING_STATUS.UNBILLED
  }
  return BILLING_STATUS.BILLED
}

/**
 * Determine charge status from API response
 * - Collected: charge exists AND canceled_at IS NULL
 * - Uncollected: charge is null OR canceled_at has value
 * @param {Object} chargeData - Charge object from API
 * @returns {string} CHARGE_STATUS value
 */
export const getChargeStatus = (chargeData) => {
  if (!chargeData || chargeData.canceled_at) {
    return CHARGE_STATUS.UNCOLLECTED
  }
  return CHARGE_STATUS.COLLECTED
}

/**
 * Get user display name from user object
 * @param {Object} user - User object with first_name, last_name
 * @returns {string} Formatted user name or empty string
 */
export const formatUserName = (user) => {
  if (!user) return ''
  const { first_name, last_name, email } = user
  if (first_name || last_name) {
    return `${first_name || ''} ${last_name || ''}`.trim()
  }
  return email || ''
}

/**
 * Get currency symbol for display
 * @param {string} currency - Currency code (USD, ARS, etc)
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currency) => {
  const symbols = {
    USD: '$',
    ARS: '$',
    UYU: '$',
    EUR: '€',
    BRL: 'R$'
  }
  return symbols[currency] || currency
}

/**
 * Format amount with currency
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @returns {string} Formatted amount with symbol
 */
export const formatAmount = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '-'
  const symbol = getCurrencySymbol(currency)
  return `${symbol} ${parseFloat(amount).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

/**
 * Format date for display
 * @param {string} dateString - Date in ISO format or 'YYYY-MM-DD'
 * @returns {string} Formatted date (DD/MM/YYYY)
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-'
  try {
    const [year, month, day] = dateString.substring(0, 10).split('-')
    return `${day}/${month}/${year}`
  } catch (e) {
    return '-'
  }
}

/**
 * Format datetime for display
 * @param {string} dateTimeString - DateTime in ISO format
 * @returns {string} Formatted datetime (DD/MM/YYYY HH:mm)
 */
export const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '-'

  try {
    const date = new Date(dateTimeString)
    return date.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (e) {
    return '-'
  }
}
